import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { MAX_REFERENCES, readProposalReference, type ProposalReference } from "@/lib/proposalReferences";

export default function ProposalReferenceUpload({ value, onChange }: { value: ProposalReference[]; onChange: (value: ProposalReference[]) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const addFiles = async (files: FileList | null) => {
    if (!files?.length || reading) return;
    setError("");
    if (files.length + value.length > MAX_REFERENCES) {
      setError(`Choose up to ${MAX_REFERENCES} reference documents.`);
      return;
    }
    setReading(true);
    const added: ProposalReference[] = [];
    try {
      for (const file of Array.from(files)) {
        const reference = await readProposalReference(file);
        added.push(reference);
        if (reference.truncated) toast.info(`${file.name}: the first 50 pages or 20,000 characters will be used.`);
      }
      onChange([...value, ...added]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The document could not be read. Try another PDF or Word document.");
    } finally {
      setReading(false);
      if (input.current) input.current.value = "";
    }
  };
  return <section className="border-t border-border pt-4 space-y-3" aria-label="Proposal reference documents">
    <h3 className="font-semibold text-sm text-foreground flex items-center gap-2"><Paperclip className="h-4 w-4 text-primary" />Reference Documents</h3>
    <input ref={input} type="file" accept=".pdf,.docx" multiple className="hidden" aria-label="Upload reference documents" onChange={(event) => void addFiles(event.target.files)} />
    <Button variant="outline" className="w-full" disabled={reading || value.length >= MAX_REFERENCES} onClick={() => input.current?.click()}>
      {reading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Paperclip className="h-4 w-4 mr-2" />}{reading ? "Reading document…" : "Add PDF or Word document"}
    </Button>
    <p className="text-xs text-muted-foreground">PDF / DOCX · 20 MB per file · Up to 3 documents</p>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {value.map((reference) => <div key={reference.id} className="border border-border rounded-md p-3 space-y-2">
      <div className="flex items-start gap-2">
        <FileText className="h-4 w-4 text-primary shrink-0 mt-1" />
        <div className="min-w-0 flex-1"><p className="text-sm font-medium break-all">{reference.name}</p><p className="text-xs text-muted-foreground">Ready · {reference.text.length.toLocaleString()} characters{reference.truncated ? " · Limited excerpt" : ""}</p></div>
        <Button variant="ghost" size="icon" disabled={reading} aria-label={`Remove ${reference.name}`} title={`Remove ${reference.name}`} onClick={() => onChange(value.filter((item) => item.id !== reference.id))}><X className="h-4 w-4" /></Button>
      </div>
      <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">View extracted text</summary><p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap break-words">{reference.text}</p></details>
    </div>)}
  </section>;
}